"use client";

import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import {
  AiChatToolShowVisitsOutput,
  AiChatToolShowVisitsPart,
  Visit
} from "@gettactic/api";
import { StatusBadge } from "@gettactic/components/src/data-display/StatusBadge";
import { useAuthenticated } from "@gettactic/hooks";
import { useQuery } from "@tanstack/react-query";
import { format, parse } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React from "react";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import { TessaMessageRendererProps } from "@/features/tessa/types";
import { api } from "@/lib/api/api";

const statusColor = (status: Visit["status"]): "green" | "blue" | "neutral" => {
  switch (status) {
    case "checked_in":
      return "green";
    case "expected":
      return "blue";
    default:
      return "neutral";
  }
};

const statusLabel = (status: Visit["status"]): string => {
  switch (status) {
    case "checked_in":
      return "Checked In";
    case "checked_out":
      return "Checked Out";
    case "expected":
      return "Expected";
    default:
      return status;
  }
};

type OfficeTimeZone = { id: string; time_zone?: string | null };

// Tessa's date is the office's day, so the list is read in the office's time zone (the user's
// default office's when no office is given), falling back to the browser's
export const visitsTimeZone = (
  officeId: string | undefined,
  offices: OfficeTimeZone[],
  defaultOfficeId: string | null | undefined,
  browserTimeZone: string
): string =>
  offices.find((office) => office.id === (officeId || defaultOfficeId))
    ?.time_zone || browserTimeZone;

// A visit's time on the clock of the office it's in
export const visitTime = (
  visit: Pick<Visit, "checked_in" | "expected" | "office_id">,
  offices: OfficeTimeZone[],
  fallbackTimeZone: string
): string => {
  const time = visit.checked_in ?? visit.expected;
  if (!time) return "";
  const timeZone =
    offices.find((office) => office.id === visit.office_id)?.time_zone ||
    fallbackTimeZone;
  return formatInTimeZone(new Date(time), timeZone, "p");
};

export const ChatBotToolShowVisits: React.FC<
  TessaMessageRendererProps<AiChatToolShowVisitsPart>
> = ({ part }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowVisitsOutput
    | undefined;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const offices = authenticatedUser.offices?.offices ?? [];
  const timeZone = visitsTimeZone(
    output?.office_id,
    offices,
    authenticatedUser.offices?.officeId,
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const date = output?.date;

  const { data: visits, isLoading } = useQuery({
    queryKey: [
      "visits.list",
      { date, office_id: output?.office_id ?? null, timeZone }
    ],
    queryFn: async () => {
      const response = await api.client.visits.list({
        date_from: date,
        date_to: date,
        office_id: output?.office_id || undefined,
        tz: timeZone,
        limit: 100
      });
      return response.result?.elements ?? [];
    },
    enabled: !!date
  });

  if (!output || isLoading) return <TacticLoader className="opacity-60" />;

  const shown = (visits ?? []).filter(
    (visit) => !output.visit_ids?.length || output.visit_ids.includes(visit.id)
  );
  const day = date ? parse(date, "yyyy-MM-dd", new Date()) : null;

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      <div className="flex flex-col gap-3">
        {day ? (
          <span className="font-medium text-neutral-500 text-xs uppercase tracking-wide">
            Visitors · {format(day, "EEE, MMM d")}
          </span>
        ) : null}
        {shown.length === 0 ? (
          <p className="text-neutral-600 text-sm">No visitors that day.</p>
        ) : (
          <ul className="flex flex-col divide-y divide-neutral-200">
            {shown.map((visit) => (
              <li
                key={visit.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="flex min-w-0 flex-col text-sm">
                  <span className="truncate font-medium text-neutral-900">
                    {visit.visitor.name || visit.visitor.email}
                  </span>
                  <span className="truncate text-neutral-500">
                    {[
                      visitTime(visit, offices, timeZone),
                      visit.visitor_type?.name,
                      visit.host?.name
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
                <StatusBadge
                  color={statusColor(visit.status)}
                  label={statusLabel(visit.status)}
                  size="sm"
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </ChatBotToolCard>
  );
};
